import { observer } from "mobx-react";
import {
  Button,
  ButtonGroup,
  Checkbox,
  HStack,
  Progress,
  Spacer,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { nodeReferences } from "../../../../../osaway/stores/OsaNodeStore";
import { NodeItem } from "./NodeItem";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { scrollToItem } from "../../../../../shared/utils/scroll-utils";
import { RouteMatchingControls } from "./RouteMatchingControls";
import { PageControls } from "./PageControls";
import { CopyNodesModal } from "./CopyNodesModal/CopyNodesModal";
import { useStores } from "../../../../../shared/stores/RootStore";
import { ProgressState } from "../../../../../shared/models/JobState";
import { NodePhotoModal } from "../../../../../shared/views/NodePhotoModal/NodePhotoModal";
import { MapMode } from "../../../../../project/stores/MapStore";

const i18prefix = "map:bottomBar.osaWay";
const pageSize = 20;
const listId = "nodeList";

const NodeListBase: React.FC = () => {
  const { osaWayStore, osaNodeStore, mapStore } = useStores();
  const osaWay = osaWayStore.getSelected()!;
  const osaNodes = osaNodeStore.nodesByWayId.get(osaWay.id!)!;
  const selectedOsaNode = osaNodeStore.getSelected();
  const copyOsaNodeModal = useDisclosure();
  const osaNodePhotoModal = useDisclosure();

  const [pageIndex, setPageIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletionProgress, setDeletionProgress] = useState<ProgressState>();
  const [lastClickedIndex, setLastClickedIndex] = useState<number | null>(null);

  const firstItemIndex = pageIndex * pageSize;
  const lastItemIndex = Math.min(firstItemIndex + pageSize, osaNodes.length);

  const page = osaNodes.slice(firstItemIndex, lastItemIndex);

  useEffect(() => {
    if (osaWay.permissions.canUpdate) {
      mapStore.setMode(MapMode.ManageNodes);
    }
    return () => {
      mapStore.setMode(MapMode.ProjectPage);
      osaNodeStore.clearPendingCoordChanges();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setPageIndex(0);
    // eslint-disable-next-line
  }, [osaWay]);

  useEffect(() => {
    if (selectedOsaNode && page.find((n) => n.id === selectedOsaNode.id)) {
      scrollToItem(selectedOsaNode.id!);
    } else {
      scrollToItem(page[0]?.id);
    }
    // eslint-disable-next-line
  }, [pageIndex, selectedOsaNode]);

  useEffect(() => {
    if (selectedOsaNode) {
      const nodeReference = nodeReferences.get(selectedOsaNode.id)!;
      if (
        nodeReference.index < firstItemIndex ||
        nodeReference.index >= lastItemIndex
      ) {
        setPageIndex(Math.floor(nodeReference.index / pageSize));
      }
    }
    // eslint-disable-next-line
  }, [selectedOsaNode]);

  useEffect(() => {
    osaNodeStore.setMultiSelectedIds(new Set());
    setLastClickedIndex(null);
    // eslint-disable-next-line
  }, [osaWay.id]);

  const selectAll = (value: boolean) => {
    if (value) {
      osaNodeStore.setMultiSelectedIds(new Set(osaNodes.map((n) => n.id)));
    } else {
      osaNodeStore.setMultiSelectedIds(new Set());
    }
  };

  const updateRangeSelection = (
    fromIndex: number,
    toIndex: number,
    checked: boolean,
  ) => {
    const start = Math.min(fromIndex, toIndex);
    const end = Math.max(fromIndex, toIndex);

    const newSet = new Set(osaNodeStore.multiSelectedIds);

    for (let i = start; i <= end; i++) {
      const node = osaNodes[i];
      if (!node) continue;

      if (checked) newSet.add(node.id);
      else newSet.delete(node.id);
    }

    osaNodeStore.setMultiSelectedIds(newSet);
  };

  const handleNodeCheckboxChange = (
    nodeId: string,
    globalIndex: number,
    checked: boolean,
    shiftKey: boolean,
  ) => {
    if (shiftKey && lastClickedIndex !== null) {
      updateRangeSelection(lastClickedIndex, globalIndex, checked);
    } else {
      const newSet = new Set(osaNodeStore.multiSelectedIds);
      if (checked) newSet.add(nodeId);
      else newSet.delete(nodeId);
      osaNodeStore.setMultiSelectedIds(newSet);
    }

    setLastClickedIndex(globalIndex);
  };

  const deleteSelectedNodes = () => {
    if (window.confirm(t(`${i18prefix}.deleteConfirm`))) {
      setIsSubmitting(true);

      const totalNodes = osaNodeStore.multiSelectedIds.size;
      let deletedNodes = 0;

      setDeletionProgress({
        value: deletedNodes,
        total: totalNodes,
      });

      osaNodeStore
        .deleteSelectedNodes(() => {
          deletedNodes++;
          setDeletionProgress({ value: deletedNodes, total: totalNodes });
        })
        .then(() => {
          osaWayStore.compileOsaWayNodes(osaWay.id);
        })
        .finally(() => {
          setIsSubmitting(false);
          setDeletionProgress(undefined);
        });
    }
  };

  return (
    <Stack gap={0} flexShrink={0}>
      <HStack p={2} pb={0}>
        <HStack>
          <Checkbox.Root
            p={2}
            checked={osaNodeStore.multiSelectedIds.size === osaNodes.length}
            onCheckedChange={(e: any) => {
              selectAll(!!e.checked);
            }}
            colorPalette="primary"
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
            <Checkbox.Label>
              <Text fontSize={"sm"}>
                {t(`${i18prefix}.nodesSelected`, {
                  count: osaNodeStore.multiSelectedIds.size,
                })}
              </Text>
            </Checkbox.Label>
          </Checkbox.Root>

          <RouteMatchingControls />

          <ButtonGroup colorPalette="primary" variant="outline" size="sm">
            <Button
              disabled={!osaNodeStore.multiSelectedIds.size}
              onClick={copyOsaNodeModal.onOpen}
              loading={isSubmitting}
            >
              {t(`${i18prefix}.copy`)}
            </Button>

            <Button
              disabled={!osaNodeStore.multiSelectedIds.size}
              loading={!!deletionProgress}
              onClick={deleteSelectedNodes}
              colorPalette={"red"}
            >
              {t(`${i18prefix}.delete`)}
            </Button>
          </ButtonGroup>

          {!!deletionProgress && (
            <ProgressBar progressState={deletionProgress} />
          )}
        </HStack>
        <Spacer />

        <PageControls
          pageSize={pageSize}
          nItems={osaNodes.length}
          setPageIndex={setPageIndex}
          pageIndex={pageIndex}
        />
      </HStack>

      <HStack flexGrow={1} overflow="auto" p={2} id={listId}>
        {page.map((osaNode, idxInPage) => {
          const globalIndex = firstItemIndex + idxInPage;

          return (
            <NodeItem
              key={osaNode.id}
              osaNode={osaNode}
              index={globalIndex}
              isChecked={osaNodeStore.multiSelectedIds.has(osaNode.id)}
              onCheckboxChange={handleNodeCheckboxChange}
              expandPhoto={() => osaNodePhotoModal.onOpen()}
            />
          );
        })}
      </HStack>

      {copyOsaNodeModal.open && (
        <CopyNodesModal onClose={copyOsaNodeModal.onClose} />
      )}
      {osaNodePhotoModal.open && (
        <NodePhotoModal onClose={osaNodePhotoModal.onClose} />
      )}
    </Stack>
  );
};

export const NodeList = observer(NodeListBase);

const ProgressBar = ({
  progressState,
}: {
  progressState: ProgressState | undefined;
}) => {
  return (
    <Progress.Root
      width={"140px"}
      size={"md"}
      value={progressState?.value ?? null}
      max={progressState?.total}
      striped
      animated
      colorPalette="red"
    >
      <Progress.Track>
        <Progress.Range />
      </Progress.Track>
      {!!progressState && (
        <Progress.ValueText>
          {progressState.value} / {progressState.total}
        </Progress.ValueText>
      )}
    </Progress.Root>
  );
};
